import WRAP from "WRAP-JS";
import { _groupBy, _orderBy, formatTimeZoneDate, maxBy, minBy, parseUTCDate } from "../../../utils/FPEUtils";
import LegendVaa from "../legend/LegendVaa";
import { configVaaAddingLayer, configVaaDh, configVaaLayer } from "./config/ConfigVaa";
import { WX_ID_VAA } from "../../../store/WeatherContentsState";
import { COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER, COMMUNICATE_ERROR_MESSAGE_VOLASH_ADVSRY } from "../../../constants/ErrorMessage";

const THRESHOLD = 24 * 3600 * 1000;
const SCHEDULED_REPORT_INTERVAL = 6 * 3600 * 1000;
// const STOP_MESSAGES = [
//   "NXT ADVISORY: NO FURTHER ADVISORY",
//   "NXT ADVISORY: NO FURTHER ADVISORIE",
//   "NXT ADVISORY: NO FURTHER ADVISORIES",
//   "NXT ADVISORY: NO FURTHER ADVISORIS",
// ];
class Vaa {
  static NAME = WX_ID_VAA;

  dh;
  layer;
  ready = false;
  dhAutoUpdate = true;
  layerConfig;
  addingLayerConfig;
  altitude;
  baseTime = '---';
  dispatcher;
  visible;
  errorMessageInfo;

  constructor(altitude) {
    this.altitude = altitude;
    this.layer = new WRAP.Geo.Layer(Vaa.NAME);
    this.layer.setVisible(false);
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
  }

  async initialize() {
    await this.initData();
    this.initLayer();
    this.ready = true;
  }

  initData() {
    return new Promise(resolve => {
      this.dh = new WRAP.Data(configVaaDh);

      this.dh.updated('data', ref => {
        if (this.errorMessageInfo) {
          this.dispatcher({type: 'CLEAR_ERROR', payload: this.errorMessageInfo});
          this.errorMessageInfo = null;
        }
        this._filterWRAPData(ref);
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
        resolve();
      }, () => {
        this.errorMessageInfo = this.dispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_VOLASH_ADVSRY,
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
        }});
      });
    });
  }

  initLayer() {
    this.layerConfig = configVaaLayer;
    this.addingLayerConfig = configVaaAddingLayer;

    this.layer.configure(this.dh, configVaaLayer);
    this.layer.updated(this._addingDrawLayer.bind(this));
    this.layer.setTooltip(this._toolTip.bind(this));
    this.layer.setAttributes({
      filter: this._visibleFilter.bind(this)
    });
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (!this.ready) {
      await this.initialize();
    }
    this.setVisible(this.visible);
  }

  getLayer() {
    return this.layer;
  }

  setVisible(visible) {
    if (!this.ready) return;

    if (this.layer.visible() === visible) {
      return;
    }
    if (visible) {
      this.layer.show();
    } else {
      this.layer.hide();
    }

    if (this.dhAutoUpdate !== visible) {
      this.dh.setAutoUpdate('data', visible);
      this.dhAutoUpdate = visible;
    }

    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
  }

  getLegend() {
    return {
      id: Vaa.NAME,
      label: 'VAA',
      component: LegendVaa,
      contents: this
    };
  }

  getTimeInformation() {
    return {
      id: Vaa.NAME,
      label: 'VAA',
      baseTime: this.baseTime
    }
  }

  applyAltitude(altitude) {
    this.altitude = altitude;
    this.layer.invalidate();
    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
  }

  changeRoutePointEditing(routePointEditing) {
    this.layer.disableInteraction(routePointEditing);
  }

  _filterWRAPData(ref) {
    let data = ref;
    if (!data || !Array.isArray(data.data)) {
      data = { data: [] };
    }

    const now = Date.now();
    let maxTime;
    const features = data.data.reduce((accumulator, smithonian) => {
      // 24時間以内のデータでフィルタ
      const tmp1 = smithonian.filter(vaac => now - parseUTCDate(vaac.valid_time).getTime() <= THRESHOLD);
      if (tmp1.length === 0) {
        return accumulator;
      }

      // latestTimeから6時間以内のデータフィルタ
      const latestDate = maxBy(tmp1, vaac => vaac.valid_time).valid_time;
      const latestDateTime = parseUTCDate(latestDate).getTime();
      const tmp2 = tmp1.filter(vaac => latestDateTime - parseUTCDate(vaac.valid_time).getTime() < SCHEDULED_REPORT_INTERVAL);

      const tooltip = tmp2.flatMap(vaac => vaac.data.features[0].properties);
      tmp2.forEach(vaac => {
        if (!maxTime || maxTime < vaac.valid_time) {
          maxTime = vaac.valid_time;
        }
        let baseProperties;
        vaac.data.features.forEach((feature, i) => {
          feature.areano = vaac.areano;
          let tau = "";
          if (feature.geometry && feature.geometry.type === 'Point') {
            baseProperties = feature.properties;
          }
          if ('ANLSIS' === feature.properties.data_type) {
            tau = "act";
            feature.properties.fcst_tau = 0;
          } else if ('EST' === feature.properties.data_type) {
            tau = "est";
            feature.properties.fcst_tau = 0;
          } else if (feature.properties.data_type === 'FCAS') {
            if ('FT' in feature.properties) {
              const hour = feature.properties.FT;
              tau = `${hour}h`;
              feature.properties.fcst_tau = hour;
            }
          }
          if (tau) {
            feature.properties.color_type = "latest_" + tau;
            //polygonにはvaacとnameがないので補填
            feature.properties.OFFICE = baseProperties.OFFICE;
            feature.properties.ENAME = baseProperties.ENAME;
            feature.properties.announced_date = baseProperties.announced_date;
          }
        })
      })

      const tmp3 = tmp2.flatMap(vaac => vaac.data.features).reduce((a, b) => {
        if (b.geometry && b.geometry.type === 'Point') {
          if (b.properties.announced_date !== latestDate) {
            return a;
          }
          b.data = tooltip;
        }
        a.push(b);
        return a;
      }, []);

      accumulator.push(...tmp3);

      return accumulator;
    }, []);

    const sortedFutures = _orderBy(features, [(feature) => {
      if(feature.properties.fcst_tau === 0) {
        return 4;
      } else if(feature.properties.fcst_tau === 6) {
        return 3;
      } else if(feature.properties.fcst_tau === 12) {
        return 2;
      } else if(feature.properties.fcst_tau === 18) {
        return 1;
      } else if(feature.geometry && feature.geometry.type === 'Point') {
        return 0;
      } else {
        feature.properties.display_flag = false;
        return -1;
      }
    }, 'announced_date']);

    if(sortedFutures.length > 0) {
      this.baseTime = maxTime;
    } else {
      this.basetime = 'No Data'
    }

    ref.type = "FeatureCollection";
    ref.features = sortedFutures;
    return;
  }

  _visibleFilter(feature) {
    //高度で噴煙をフィルタリング
    if (feature.geometry && feature.geometry.type !== 'Polygon') {
      return true;
    }

    const fl1 = feature.properties.layer_height_high;
    const fl2 = feature.properties.layer_height_low;

    // 高度情報が共に存在しなければフィルタリングはしない
    if (!fl1 && !fl2) {
      return true;
    }

    const low = fl2 ? fl2 / 100 : -1;
    const high = fl1 ? fl1 / 100 : -1;
    if (high !== -1 && low === -1 && this.altitude[0] > high) {
      return false;
    } else if (high === -1 && low !== -1 && this.altitude[1] < low) {
      return false;
    } else if (high !== -1 && low !== -1) {
      if (this.altitude[0] > high || this.altitude[1] < low) {
        return false;
      }
    }
    return true;
  }

  _addingDrawLayer() {
    const data = this.dh.retained.data.features;
    const layer = this.layer;
    const config = this.addingLayerConfig['default'];
    const smithonianGroup = _groupBy(data, 'areano');

    const toInt = v => { return v && parseInt(v); };

    for (const features of Object.values(smithonianGroup)) {
      const polygons = features.reduce((accumulator, feature) => {
        if (feature.geometry && feature.geometry.type === 'Polygon') {
          if (this._visibleFilter(feature)) {
            accumulator.push(feature);
          }
        }
        return accumulator;
      }, []);

      //smithonianの中でlatestなannounced_dateを持つPoint
      const p = features.reduce((accumulator, feature) => {
        if (feature.geometry && feature.geometry.type === 'Point') {
          return accumulator.announced_date > feature.announced_date ? accumulator : feature;
        }
        return accumulator;
      }, {});

      let offsetY = config.offsetY;
      const point = p.geometry.coordinates;

      [
        ['fcst_tau', 0],
        ['fcst_tau', 6],
        ['fcst_tau', 12],
        ['fcst_tau', 18],
      ].forEach(cond => {
        const tau = polygons.filter(polygon => {
          return polygon.properties[cond[0]] === cond[1];
        });
        if(tau.length === 0) {
          return;
        }

        const low = minBy(tau, (o)=>{
          return toInt(o.properties.HGT_LOW);});

        const high = maxBy(tau, (o) => {
          return toInt(o.properties.HGT_HIGH);
        });

        // 高度情報が共に存在しなければラベルは表示しない.
        if(!low && !high) return;

        // 片方だけ存在しない場合は、-- を表示.
        const text = getAltitude(low.properties.HGT_LOW, high.properties.HGT_HIGH);

        // Style (文字色) 取得.
        const base = high || low;
        const arg = base.properties.color_type ? {
          selector: {
            key: 'color_type',
            value: base.properties.color_type.replace(/^[^_]+/, 'latest')
          }
        } : (feature) => {
          //TODO このコードが謎。そもそもcolor_typeが設定されないケースがあるのか？
          return !feature.selector;
        };
        const style = this.layerConfig.Attributes.features.find(feature => {
          return feature.selector.value === arg.selector.value;
        }).style.default;

        let fillStyle;
        if (style.text_color) {
          fillStyle = style.text_color;
        } else {
          fillStyle = style.fillStyle ? style.fillStyle : style.line_color;
        }

        // 噴煙拡散予想の高度ラベル表示.
        const label = new WRAP.Geo.Feature.Text(Object.assign({}, config.alt, {
          fillStyle: fillStyle,
          point: point,
          text: text,
          offsetY: offsetY
        }));
        label.data = tau;
        layer.addFeature(label);

        offsetY -= config.alt.fontSize;
      });
    }
  }

  _toolTip(geo)  {
    if (!geo) return;
    if (geo.geometry.type === 'Point') {
      const dataList = _orderBy(geo.data, "announced_date", "desc");
      const tooltip = ['<div style="text-align: left; max-width: 450px;">'];
      dataList.forEach((p, i) => {
        if (i === 0) {
          const name = geo.properties.ENAME;
          tooltip.push(`Volcano : ${name}`);
        } else {
          tooltip.push('<hr/>');
        }
        const vaac = p.OFFICE;
        const announce = p.announced_date ? formatTimeZoneDate(p.announced_date) : '---';
        const t = `<ul>
                  <li>VAAC : ${vaac}</li>
                  <li>Announce : ${announce}</li>
                  <li style="font-size: 11px">${p.bulletin}</li>
                </ul>`
        tooltip.push(t);
      })
      tooltip.push('</div>');
      return tooltip.join('');
    } else if (geo.geometry.type === 'Polygon') {
      const name = geo.properties.ENAME;
      const vaac = geo.properties.OFFICE;
      const announce = formatTimeZoneDate(geo.properties.announced_date);
      const valid = geo.properties.validtime ? formatTimeZoneDate(geo.properties.validtime) : '---';
      const altitude = getAltitude(geo.properties.HGT_LOW, geo.properties.HGT_HIGH);
      return `<div style="text-align: left; max-width: 250px;">
                <ul>
                  <li>Volcano : ${name}</li>
                  <li>VAAC : ${vaac}</li>
                  <li>Announce : ${announce}</li>
                  <li>Valid: ${valid}</li> 
                  <li>${altitude}</li>
                </ul>       
              </div>`;
    }
  }

  _getSelectedFlightLevel() {
    const low = Math.min(...this.altitude);
    const high = Math.max(...this.altitude);
    let fl;
    if(low === 0) {
      fl = 'SFC';
    } else {
      fl = `FL${('000' + low).slice(-3)}`;
    }
    if(low !== high) {
      fl += ` - FL${('000' + high).slice(-3)}`;
    }
    return fl;
  }
}

const getAltitude = (low, high) => {
  const fl1 = high;
  const fl2 = low;

  if (fl1 === undefined && fl2 === undefined) {
    return '';
  }

  const _low = fl2 !== undefined ? fl2 / 100 : ' - ';
  const _high = fl1 !== undefined ? fl1 / 100 : ' - ';

  if (_low === _high) {
    return 'FL' + _low;
  } else {
    if (_low === 0) {
      return 'SFC/FL' + _high;
    }
    return 'FL' + _low + '/' + _high;
  }
}

export default Vaa;
